import React from 'react';
import { PrismSelect } from '@prism-ui/react';

interface MonthYearPickerProps {
  value: string | null;
  onChange: (isoDate: string | null) => void;
  required?: boolean;
  disabled?: boolean;
}

const MONTHS = [
  { value: '1', display: 'January' },
  { value: '2', display: 'February' },
  { value: '3', display: 'March' },
  { value: '4', display: 'April' },
  { value: '5', display: 'May' },
  { value: '6', display: 'June' },
  { value: '7', display: 'July' },
  { value: '8', display: 'August' },
  { value: '9', display: 'September' },
  { value: '10', display: 'October' },
  { value: '11', display: 'November' },
  { value: '12', display: 'December' },
];

const currentYear = new Date().getFullYear();
const START_YEAR = 1980;
const YEARS = Array.from({ length: currentYear - START_YEAR + 2 }, (_, i) => ({
  value: String(START_YEAR + i),
  display: String(START_YEAR + i),
}));

const parseValue = (value: string | null): { month: string; year: string } => {
  if (!value) return { month: '', year: '' };
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return { month: '', year: '' };
    return {
      month: String(date.getUTCMonth() + 1),
      year: String(date.getUTCFullYear()),
    };
  } catch {
    return { month: '', year: '' };
  }
};

const MonthYearPicker: React.FC<MonthYearPickerProps> = ({ value, onChange, required = false, disabled = false }) => {
  const parsed = parseValue(value);
  const [localMonth, setLocalMonth] = React.useState(parsed.month);
  const [localYear, setLocalYear] = React.useState(parsed.year);

  // Refs keep the latest values to prevent stale closures when
  // PrismSelect fires prismChange on programmatic value updates.
  const monthRef = React.useRef(parsed.month);
  const yearRef = React.useRef(parsed.year);

  // Sync local state when prop value changes externally (e.g. reset)
  React.useEffect(() => {
    const p = parseValue(value);
    setLocalMonth(p.month);
    setLocalYear(p.year);
    monthRef.current = p.month;
    yearRef.current = p.year;
  }, [value]);

  const handleMonthChange = (e: any) => {
    // PrismSelect may emit detail as a string or { value: string }
    const raw = e.detail?.value ?? e.detail;
    const newMonth = String(raw ?? '');
    // Ignore echo events where PrismSelect re-fires on programmatic value set
    if (newMonth === monthRef.current) return;
    setLocalMonth(newMonth);
    monthRef.current = newMonth;
    if (!newMonth) {
      onChange(null);
    } else if (yearRef.current) {
      onChange(`${yearRef.current}-${newMonth.padStart(2, '0')}-01`);
    }
  };

  const handleYearChange = (e: any) => {
    const raw = e.detail?.value ?? e.detail;
    const newYear = String(raw ?? '');
    if (newYear === yearRef.current) return;
    setLocalYear(newYear);
    yearRef.current = newYear;
    if (!newYear) {
      onChange(null);
    } else if (monthRef.current) {
      onChange(`${newYear}-${monthRef.current.padStart(2, '0')}-01`);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <div style={{ flex: '1 1 60%' }}>
        <PrismSelect
          label="Month"
          value={localMonth}
          options={MONTHS}
          required={required}
          disabled={disabled}
          onPrismChange={handleMonthChange}
        />
      </div>
      <div style={{ flex: '1 1 40%' }}>
        <PrismSelect
          label="Year"
          value={localYear}
          options={YEARS}
          required={required}
          disabled={disabled}
          onPrismChange={handleYearChange}
        />
      </div>
    </div>
  );
};

export default MonthYearPicker;
